import {
	DATE_TIME_ISO_FORMAT,
	DateCellRenderer,
	DefaultCellRenderer,
	MoneyCellEditor,
	createValueSetter,
	SelectCellEditor,
	translate as t,
	createTypedOptionsValueSetter,
} from '@aleph/shared-components';
import {
	SuperGridColDef,
	ColumnModeTypeEnum,
	SelectCellRenderer,
} from '@aleph/supergrid';
import {
	startCase, toLower,
} from 'lodash';
import dayjs from 'dayjs';
import { groupHeaderNames } from 'wb-reddit/app/routes/superGrid/columnDefinitions/headerNames';
import DateTimeCellEditor from 'wb-reddit/app/routes/superGrid/cellEditors/datetime/datetimeCellEditor';
import {
	GroupAssetFormat, GroupBudgetType, GroupFrequencyUnit,
	GroupPacing, GroupSuperGridListQueryItems, GroupTargetsGender, GroupTargetsPlacement, GroupTargetsPlatform,
	GroupTargetsPodcastEpisodeTopic,
	GeoType,
	SupergridGroupCategoriesDocument,
	SupergridGroupTargetsArtistsDocument,
	SupergridGroupTargetsGenresDocument,
	SupergridGroupTargetsGeosDocument,
	SupergridGroupTargetsInterestsDocument,
	SupergridGroupTargetsLanguagesDocument,
	SupergridGroupTargetsPlaylistsDocument,
	SupergridGroupTargetsSensitiveTopicsDocument,
} from 'wb-spotify/graphql/generated';
import MoneyCellRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/money';
import AsyncCellSelectEditor from 'wb-spotify/app/routes/superGrid/cellEditors/asyncCellSelectEditor';
import AsyncGeoCellSelectEditor from 'wb-spotify/app/routes/superGrid/cellEditors/asyncGeoCellSelectEditor';
import { editableOnThisLevel } from 'wb-spotify/app/routes/superGrid/helpers';
import { divideCurrencyFields } from 'wb-spotify/app/routes/superGrid/common/constants';
import AgeRangeSelectCellEditor from 'wb-spotify/app/routes/superGrid/cellEditors/ageRangeCellEditor';
import GeoSelectCellRenderer from 'wb-spotify/app/routes/superGrid/cellRenderers/geoSelectCellRenderer';

export const GroupGenderOptions = Object.values(GroupTargetsGender).map(gender => ({
	label: t(
		`sp: supergrid groupTargetsGenders.${gender}`,
		// @ts-ignore TODO fix types
		startCase(toLower(gender === GroupTargetsGender.NonBinary
			? 'All (includes non-binary and more)' : gender)),
	),
	value: gender,
}));

export const GroupPlatformOptions = Object.values(GroupTargetsPlatform).map(platform => ({
	// @ts-ignore TODO fix types
	label: t(`sp: supergrid groupTargetsPlatforms.${platform}`, startCase(toLower(platform))),
	value: platform,
}));

export const GroupPlacementsOptions = Object.values(GroupTargetsPlacement).map(placement => ({
	// @ts-ignore TODO fix types
	label: t(`sp: supergrid groupTargetsPlacements.${placement}`, startCase(toLower(placement))),
	value: placement,
}));

export const GROUP_ASSET_FORMAT_OPTIONS = Object.values(GroupAssetFormat).map(format => ({
	// @ts-ignore TODO fix types
	label: t(`sp: supergrid groupAssetFormat.${format}`, startCase(toLower(format))),
	value: format,
}));

export const GROUP_FREQUENCY_CAP_PER_DAY_OPTIONS = Array.from({ length: 5 }, (_, i) => ({
	value: i + 1,
	label: `${i + 1}`,
}));

export const GROUP_FREQUENCY_CAP_PER_WEEK_OPTIONS = Array.from({ length: 35 }, (_, i) => ({
	value: i + 1,
	label: `${i + 1}`,
}));

export const GROUP_FREQUENCY_CAP_PER_MONTH_OPTIONS = Array.from({ length: 50 }, (_, i) => ({
	value: i + 1,
	label: `${i + 1}`,
}));

export const GROUP_PACING_OPTIONS = [
	{
		value: GroupPacing.PacingAsap,
		label: 'Accelerated',
	},
	{
		value: GroupPacing.PacingEven,
		label: 'Standard',
	},
];

export const GROUP_BID_TYPE_OPTIONS = [
	{
		value: GroupBudgetType.Daily,
		label: 'Daily',
	},
	{
		value: GroupBudgetType.Lifetime,
		label: 'Lifetime',
	},
];

export const GROUP_PODCAST_EPISODE_TOPIC_OPTIONS = Object.values(GroupTargetsPodcastEpisodeTopic).map(topic => ({
	// TODO: This is also WIP on the BE, before it was a query now its enum
	// Will wait for final decision wether this is a live fetch on the BE or taken from enum
	// If enum, TODO: Create translations in lokalise
	// @ts-ignore TODO fix types
	label: t(`sp: supergrid groupTargetsPodcastEpisodeTopics.${topic}`, startCase(toLower(topic))),
	value: topic,
}));

export const GROUP_AGE_RANGE_OPTIONS = [
	{ value: '21-99', label: 'Age-restricted advertising (21+)' },
	{ value: '14-17', label: '14-17' },
	{ value: '18-24', label: '18-24' },
	{ value: '25-34', label: '25-34' },
	{ value: '35-44', label: '35-44' },
	{ value: '45-54', label: '45-54' },
	{ value: '55-64', label: '55-64' },
	{ value: '65-99', label: '65+' },
];

// Type of data that is passed to the grid, with additional fields added during creation
type SupergridGroupRowData = GroupSuperGridListQueryItems & { isInCreation: boolean };

// Helper function to check if countryCode is available
const hasCountryCode = (params: { data?: SupergridGroupRowData }): boolean => {
	const countryCode = params?.data?.groupTargets?.groupTargetsGeoTargets?.countryCode;

	if (!countryCode) {
		return false;
	}

	const countryCodeValue = typeof countryCode === 'object' && countryCode !== null && 'value' in countryCode
		? (countryCode as { value: string }).value
		: (countryCode as string | undefined);

	return !!countryCodeValue;
};

// Helper function to get countryCode value
const getCountryCodeValue = (params: { data?: SupergridGroupRowData }): string | undefined => {
	const countryCode = params?.data?.groupTargets?.groupTargetsGeoTargets?.countryCode;

	if (!countryCode) {
		return undefined;
	}

	return typeof countryCode === 'object' && countryCode !== null && 'value' in countryCode
		? (countryCode as { value: string }).value
		: (countryCode as string | undefined);
};

const getGroupFrequencyCapValue = (
	params: { data?: SupergridGroupRowData },
	frequencyUnit: GroupFrequencyUnit,
) => params.data?.groupFrequencyCaps?.find(cap => cap?.frequencyUnit === frequencyUnit)?.maxImpressions;

const setGroupFrequencyCapValue = (
	params: { data?: SupergridGroupRowData; newValue?: number | null | string },
	frequencyUnit: GroupFrequencyUnit,
) => {
	if (params.data?.groupFrequencyCaps) {
		if (params.newValue === undefined || params.newValue === null || params.newValue === '') {
			params.data.groupFrequencyCaps = params.data.groupFrequencyCaps
				?.filter(cap => cap?.frequencyUnit !== frequencyUnit);

			return true;
		}

		const groupFrequencyCap = params.data.groupFrequencyCaps
			?.find(cap => cap?.frequencyUnit === frequencyUnit);

		if (groupFrequencyCap) {
			groupFrequencyCap.maxImpressions = params.newValue as number;

			return true;
		}

		params.data.groupFrequencyCaps.push({
			frequencyUnit,
			maxImpressions:  params.newValue as number,
			frequencyPeriod: 1,
		});

		return true;
	}

	return false;
};

export const GROUP_COLUMN_DEFINITIONS: SuperGridColDef<SupergridGroupRowData>[] = [
	{
		columnMode:   ColumnModeTypeEnum.REGULAR,
		field:        'id',
		elasticChild: 'settingsElastic',
		cellRenderer: DefaultCellRenderer,
		rowGroup:     true,
		hide:         true,
	},
	{
		field:              'groupTargets.groupTargetsArtists',
		headerName:         'Artist on Spotify',
		cellRenderer:       SelectCellRenderer,
		editable:           editableOnThisLevel,
		cellRendererParams: (params) => ({
			labelGetter: (item) => item.label,
			disabled:    params.data.isCreate,
		}),
		elasticChild:     'settingsElastic',
		columnMode:       ColumnModeTypeEnum.BOTH,
		cellEditor:       AsyncCellSelectEditor,
		cellEditorParams: () => ({
			graphqlQuery:   SupergridGroupTargetsArtistsDocument,
			valueFieldName: 'artistName',
		}),
		valueGetter: params => params.data?.groupTargets?.groupTargetsArtists,
		valueSetter: createTypedOptionsValueSetter<SupergridGroupRowData>(
			'groupTargets.groupTargetsArtists',
			'Artist',
		),
	},
	{
		field:              'category',
		headerName:         'Ad category',
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: (params) => ({
			labelGetter: (item) => item.label,
			disabled:    params.data.isCreate,
		}),
		elasticChild:     'settingsElastic',
		columnMode:       ColumnModeTypeEnum.BOTH,
		cellEditor:       AsyncCellSelectEditor,
		cellEditorParams: () => ({
			graphqlQuery:   SupergridGroupCategoriesDocument,
			valueFieldName: 'categoryName',
			isMulti:        false,
		}),
		valueGetter: params => params.data?.category,
		valueSetter: createTypedOptionsValueSetter<SupergridGroupRowData>(
			'category',
			'Category',
		),
		editable: editableOnThisLevel,
	},
	{
		field:              'groupTargets.groupTargetsGeoTargets.countryCode',
		headerName:         'Country',
		cellRenderer:       SelectCellRenderer,
		editable:           editableOnThisLevel,
		cellRendererParams: (params) => ({
			labelGetter: (item) => item.label,
			disabled:    params.data.isCreate,
		}),
		elasticChild:     'settingsElastic',
		columnMode:       ColumnModeTypeEnum.BOTH,
		cellEditor:       AsyncCellSelectEditor,
		cellEditorParams: () => ({
			graphqlQuery:        SupergridGroupTargetsGeosDocument,
			valueFieldName:      'geoName',
			isMulti:             false,
			additionalVariables: {
				filters: {
					geoType: [
						{
							value:   [GeoType.Country],
							exclude: false,
						},
					],
				},
			},
		}),
		valueSetter: (params: any) => {
			// Ensure groupTargets exists
			if (!params.data.groupTargets) {
				params.data.groupTargets = {};
			}

			// Ensure groupTargetsGeoTargets exists
			if (!params.data.groupTargets.groupTargetsGeoTargets) {
				params.data.groupTargets.groupTargetsGeoTargets = {
					countryCode:   undefined,
					cityIds:       [],
					dmaIds:        [],
					regionIds:     [],
					postalCodeIds: [],
				};
			}

			const oldCountryCode = params.data.groupTargets.groupTargetsGeoTargets.countryCode;
			const newCountryCode = params.newValue;

			// Use the base setter to set the country code
			const baseSetter = createTypedOptionsValueSetter<SupergridGroupRowData>(
				'groupTargets.groupTargetsGeoTargets.countryCode',
				'Geo',
			);
			const result = baseSetter(params);

			// If country changed, clear all dependent geo fields
			if (result && oldCountryCode?.value !== newCountryCode?.value) {
				params.data.groupTargets.groupTargetsGeoTargets.cityIds = [];
				params.data.groupTargets.groupTargetsGeoTargets.dmaIds = [];
				params.data.groupTargets.groupTargetsGeoTargets.regionIds = [];
				params.data.groupTargets.groupTargetsGeoTargets.postalCodeIds = [];
			}

			return result;
		},
		valueGetter: params => params.data?.groupTargets?.groupTargetsGeoTargets?.countryCode,
	},
	{
		field:              'groupTargets.groupTargetsGeoTargets.cityIds',
		headerName:         'Cities',
		cellRenderer:       GeoSelectCellRenderer,
		editable:           (params) => editableOnThisLevel(params) && hasCountryCode(params),
		cellRendererParams: (params) => ({
			geoType:     GeoType.City,
			fieldName:   'cityIds',
			labelGetter: (item) => item?.label || item?.value || '',
			disabled:    params.data.isCreate || !hasCountryCode(params),
			placeholder: !hasCountryCode(params) ? 'Select country first' : undefined,
		}),
		elasticChild:     'settingsElastic',
		columnMode:       ColumnModeTypeEnum.BOTH,
		cellEditor:       AsyncGeoCellSelectEditor,
		cellEditorParams: (params) => {
			const countryCodeValue = getCountryCodeValue(params);

			if (!countryCodeValue) {
				return {
					graphqlQuery:        SupergridGroupTargetsGeosDocument,
					valueFieldName:      'geoName',
					isMulti:             true,
					additionalVariables: {
						filters: {
							geoType: [
								{
									value:   [GeoType.City],
									exclude: false,
								},
							],
						},
					},
				};
			}

			return {
				graphqlQuery:        SupergridGroupTargetsGeosDocument,
				valueFieldName:      'geoName',
				isMulti:             true,
				additionalVariables: {
					filters: {
						geoType: [
							{
								value:   [GeoType.City],
								exclude: false,
							},
						],
						geoCountryCode: [
							{
								value:   [countryCodeValue],
								exclude: false,
							},
						],
					},
				},
			};
		},
		valueGetter: params => {
			const cityIds = params.data?.groupTargets?.groupTargetsGeoTargets?.cityIds;

			// If already objects with labels, return as is
			if (Array.isArray(cityIds) && cityIds.length > 0 && typeof cityIds[0] === 'object' && cityIds[0] !== null && 'label' in cityIds[0]) {
				return cityIds;
			}

			// If strings (IDs), return as is - will be transformed by renderer/editor
			return cityIds;
		},
		valueSetter: createTypedOptionsValueSetter<SupergridGroupRowData>(
			'groupTargets.groupTargetsGeoTargets.cityIds',
			'Geo',
		),
	},
	{
		field:              'groupTargets.groupTargetsGeoTargets.dmaIds',
		headerName:         'DMAs',
		cellRenderer:       GeoSelectCellRenderer,
		editable:           (params) => editableOnThisLevel(params) && hasCountryCode(params),
		cellRendererParams: (params) => ({
			geoType:     GeoType.DmaRegion,
			fieldName:   'dmaIds',
			labelGetter: (item) => item?.label || item?.value || '',
			disabled:    params.data.isCreate || !hasCountryCode(params),
			placeholder: !hasCountryCode(params) ? 'Select country first' : undefined,
		}),
		elasticChild:     'settingsElastic',
		columnMode:       ColumnModeTypeEnum.BOTH,
		cellEditor:       AsyncGeoCellSelectEditor,
		cellEditorParams: (params) => {
			const countryCodeValue = getCountryCodeValue(params);

			if (!countryCodeValue) {
				return {
					graphqlQuery:        SupergridGroupTargetsGeosDocument,
					valueFieldName:      'geoName',
					isMulti:             true,
					additionalVariables: {
						filters: {
							geoType: [
								{
									value:   [GeoType.DmaRegion],
									exclude: false,
								},
							],
						},
					},
				};
			}

			return {
				graphqlQuery:        SupergridGroupTargetsGeosDocument,
				valueFieldName:      'geoName',
				isMulti:             true,
				additionalVariables: {
					filters: {
						geoType: [
							{
								value:   [GeoType.DmaRegion],
								exclude: false,
							},
						],
						geoCountryCode: [
							{
								value:   [countryCodeValue],
								exclude: false,
							},
						],
					},
				},
			};
		},
		valueGetter: params => params.data?.groupTargets?.groupTargetsGeoTargets?.dmaIds,
		valueSetter: createTypedOptionsValueSetter<SupergridGroupRowData>(
			'groupTargets.groupTargetsGeoTargets.dmaIds',
			'Geo',
		),
	},
	{
		field:              'groupTargets.groupTargetsGeoTargets.regionIds',
		headerName:         'Regions',
		cellRenderer:       GeoSelectCellRenderer,
		editable:           (params) => editableOnThisLevel(params) && hasCountryCode(params),
		cellRendererParams: (params) => ({
			geoType:     GeoType.Region,
			fieldName:   'regionIds',
			labelGetter: (item) => item?.label || item?.value || '',
			disabled:    params.data.isCreate || !hasCountryCode(params),
			placeholder: !hasCountryCode(params) ? 'Select country first' : undefined,
		}),
		elasticChild:     'settingsElastic',
		columnMode:       ColumnModeTypeEnum.BOTH,
		cellEditor:       AsyncGeoCellSelectEditor,
		cellEditorParams: (params) => {
			const countryCodeValue = getCountryCodeValue(params);

			if (!countryCodeValue) {
				return {
					graphqlQuery:        SupergridGroupTargetsGeosDocument,
					valueFieldName:      'geoName',
					isMulti:             true,
					additionalVariables: {
						filters: {
							geoType: [
								{
									value:   [GeoType.Region],
									exclude: false,
								},
							],
						},
					},
				};
			}

			return {
				graphqlQuery:        SupergridGroupTargetsGeosDocument,
				valueFieldName:      'geoName',
				isMulti:             true,
				additionalVariables: {
					filters: {
						geoType: [
							{
								value:   [GeoType.Region],
								exclude: false,
							},
						],
						geoCountryCode: [
							{
								value:   [countryCodeValue],
								exclude: false,
							},
						],
					},
				},
			};
		},
		valueGetter: params => params.data?.groupTargets?.groupTargetsGeoTargets?.regionIds,
		valueSetter: createTypedOptionsValueSetter<SupergridGroupRowData>(
			'groupTargets.groupTargetsGeoTargets.regionIds',
			'Geo',
		),
	},
	{
		field:              'groupTargets.groupTargetsGeoTargets.postalCodeIds',
		headerName:         'Postal Codes',
		cellRenderer:       GeoSelectCellRenderer,
		editable:           (params) => editableOnThisLevel(params) && hasCountryCode(params),
		cellRendererParams: (params) => ({
			geoType:     GeoType.PostalCode,
			fieldName:   'postalCodeIds',
			labelGetter: (item) => item?.label || item?.value || '',
			disabled:    params.data.isCreate || !hasCountryCode(params),
			placeholder: !hasCountryCode(params) ? 'Select country first' : undefined,
		}),
		elasticChild:     'settingsElastic',
		columnMode:       ColumnModeTypeEnum.BOTH,
		cellEditor:       AsyncGeoCellSelectEditor,
		cellEditorParams: (params) => {
			const countryCodeValue = getCountryCodeValue(params);

			if (!countryCodeValue) {
				return {
					graphqlQuery:        SupergridGroupTargetsGeosDocument,
					valueFieldName:      'geoId',
					isMulti:             true,
					additionalVariables: {
						filters: {
							geoType: [
								{
									value:   [GeoType.PostalCode],
									exclude: false,
								},
							],
						},
					},
				};
			}

			return {
				graphqlQuery:        SupergridGroupTargetsGeosDocument,
				valueFieldName:      'geoId',
				isMulti:             true,
				additionalVariables: {
					filters: {
						geoType: [
							{
								value:   [GeoType.PostalCode],
								exclude: false,
							},
						],
						geoCountryCode: [
							{
								value:   [countryCodeValue],
								exclude: false,
							},
						],
					},
				},
			};
		},
		valueGetter: params => params.data?.groupTargets?.groupTargetsGeoTargets?.postalCodeIds,
		valueSetter: createTypedOptionsValueSetter<SupergridGroupRowData>(
			'groupTargets.groupTargetsGeoTargets.postalCodeIds',
			'Geo',
		),
	},
	{
		field:              'groupTargets.groupTargetsAgeRanges',
		headerName:         'Age range',
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: (params) => ({
			options:  GROUP_AGE_RANGE_OPTIONS,
			isMulti:  true,
			disabled: params.data.isCreate,
		}),
		cellEditor:       AgeRangeSelectCellEditor,
		cellEditorParams: () => ({
			options: GROUP_AGE_RANGE_OPTIONS,
			isMulti: true,
		}),
		valueGetter:  params => params.data?.groupTargets?.groupTargetsAgeRanges,
		valueSetter:  createValueSetter('groupTargets.groupTargetsAgeRanges'),
		columnMode:   ColumnModeTypeEnum.BOTH,
		elasticChild: 'settingsElastic',
		editable:     editableOnThisLevel,
	},
	{
		field:              'groupTargets.groupTargetsGenders',
		headerName:         'Gender',
		elasticChild:       'settingsElastic',
		editable:           editableOnThisLevel,
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: (params) => ({
			options:  GroupGenderOptions,
			disabled: params.data.isCreate,
		}),
		cellEditor:       SelectCellEditor,
		cellEditorParams: () => ({
			options: GroupGenderOptions,
		}),
		columnMode:  ColumnModeTypeEnum.BOTH,
		valueGetter: params => params.data?.groupTargets?.groupTargetsGenders,
		valueSetter: (params) => {
			if (params.data?.groupTargets) {
				params.data.groupTargets.groupTargetsGenders = params.newValue;

				return true;
			}

			return false;
		},
	},
	{
		field:              'groupTargets.groupTargetsInterests',
		headerName:         'Interests',
		cellRenderer:       SelectCellRenderer,
		editable:           editableOnThisLevel,
		cellRendererParams: (params) => ({
			labelGetter: (item) => item.label,
			disabled:    params.data.isCreate,
		}),
		elasticChild:     'settingsElastic',
		columnMode:       ColumnModeTypeEnum.BOTH,
		cellEditor:       AsyncCellSelectEditor,
		cellEditorParams: () => ({
			graphqlQuery:   SupergridGroupTargetsInterestsDocument,
			valueFieldName: 'interestName',
		}),
		valueGetter: params => params.data?.groupTargets?.groupTargetsInterests,
		valueSetter: createTypedOptionsValueSetter<SupergridGroupRowData>(
			'groupTargets.groupTargetsInterests',
			'Interest',
		),
	},
	{
		field:              'groupTargets.groupTargetsLanguage.language',
		headerName:         'Language',
		cellRenderer:       SelectCellRenderer,
		editable:           editableOnThisLevel,
		cellRendererParams: (params) => ({
			labelGetter: (item) => item?.label,
			disabled:    params.data.isCreate,
		}),
		elasticChild:     'settingsElastic',
		columnMode:       ColumnModeTypeEnum.BOTH,
		cellEditor:       AsyncCellSelectEditor,
		cellEditorParams: () => ({
			graphqlQuery:   SupergridGroupTargetsLanguagesDocument,
			valueFieldName: 'languageName',
			isMulti:        false,
		}),
		valueGetter: params => params.data?.groupTargets?.groupTargetsLanguage?.language,
		valueSetter: createTypedOptionsValueSetter<SupergridGroupRowData>(
			'groupTargets.groupTargetsLanguage.language',
			'Language',
		),
	},
	{
		field:              'groupTargets.groupTargetsPlatforms',
		headerName:         'Devices',
		elasticChild:       'settingsElastic',
		editable:           editableOnThisLevel,
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: (params) => ({
			options:  GroupPlatformOptions,
			disabled: params.data.isCreate,
		}),
		cellEditor:       SelectCellEditor,
		cellEditorParams: () => ({
			options: GroupPlatformOptions,
			isMulti: true,
		}),
		columnMode:  ColumnModeTypeEnum.BOTH,
		valueGetter: params => params.data?.groupTargets?.groupTargetsPlatforms,
		valueSetter: (params) => {
			if (params.data?.groupTargets) {
				params.data.groupTargets.groupTargetsPlatforms = params.newValue;

				return true;
			}

			return false;
		},
	},
	{
		field:        'groupAssetFormat',
		headerName:   'Format',
		elasticChild: 'settingsElastic',
		editable(params) {
			// Only allow editing in creation
			if (params?.data?.isInCreation) {
				return editableOnThisLevel(params);
			}

			return false;
		},
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: (params) => ({
			options:  GROUP_ASSET_FORMAT_OPTIONS,
			disabled: params.data.isCreate,
		}),
		cellEditor:       SelectCellEditor,
		cellEditorParams: () => ({
			options: GROUP_ASSET_FORMAT_OPTIONS,
		}),
		columnMode:  ColumnModeTypeEnum.BOTH,
		valueGetter: params => params.data?.groupAssetFormat,
		valueSetter: (params) => {
			if (params.data?.groupAssetFormat) {
				params.data.groupAssetFormat = params.newValue;

				return true;
			}

			return false;
		},
	},
	{
		field:              'groupTargets.groupTargetsPlacements',
		headerName:         'Placements',
		elasticChild:       'settingsElastic',
		editable:           editableOnThisLevel,
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: (params) => ({
			options:  GroupPlacementsOptions,
			disabled: params.data.isCreate,
			isMulti:  true,
		}),
		cellEditor:       SelectCellEditor,
		cellEditorParams: () => ({
			options: GroupPlacementsOptions,
			isMulti: true,
		}),
		columnMode:  ColumnModeTypeEnum.BOTH,
		valueGetter: params => params.data?.groupTargets?.groupTargetsPlacements,
		valueSetter: (params) => {
			if (params.data?.groupTargets) {
				params.data.groupTargets.groupTargetsPlacements = params.newValue;

				return true;
			}

			return false;
		},
	},
	{
		field:              'groupTargets.groupTargetsGenres',
		headerName:         'Music genres',
		cellRenderer:       SelectCellRenderer,
		editable:           editableOnThisLevel,
		cellRendererParams: (params) => ({
			labelGetter: (item) => item.label,
			disabled:    params.data.isCreate,
		}),
		elasticChild:     'settingsElastic',
		columnMode:       ColumnModeTypeEnum.BOTH,
		cellEditor:       AsyncCellSelectEditor,
		cellEditorParams: () => ({
			graphqlQuery:   SupergridGroupTargetsGenresDocument,
			valueFieldName: 'genreName',
		}),
		valueGetter: params => params.data?.groupTargets?.groupTargetsGenres,
		valueSetter: createTypedOptionsValueSetter<SupergridGroupRowData>(
			'groupTargets.groupTargetsGenres',
			'Genre',
		),
	},
	{
		field:              'groupTargets.groupTargetsPlaylists',
		headerName:         'Playlist themes',
		cellRenderer:       SelectCellRenderer,
		editable:           editableOnThisLevel,
		cellRendererParams: (params) => ({
			labelGetter: (item) => item.label,
			disabled:    params.data.isCreate,
		}),
		elasticChild:     'settingsElastic',
		columnMode:       ColumnModeTypeEnum.BOTH,
		cellEditor:       AsyncCellSelectEditor,
		cellEditorParams: () => ({
			graphqlQuery:   SupergridGroupTargetsPlaylistsDocument,
			valueFieldName: 'playlistName',
		}),
		valueGetter: params => params.data?.groupTargets?.groupTargetsPlaylists,
		valueSetter: createTypedOptionsValueSetter<SupergridGroupRowData>(
			'groupTargets.groupTargetsPlaylists',
			'Playlist',
		),
	},
	{
		field:              'groupTargets.groupTargetsPodcastEpisodeTopics',
		headerName:         'Podcast topics',
		elasticChild:       'settingsElastic',
		editable:           editableOnThisLevel,
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: (params) => ({
			options:  GROUP_PODCAST_EPISODE_TOPIC_OPTIONS,
			disabled: params.data.isCreate,
			isMulti:  true,
		}),
		cellEditor:       SelectCellEditor,
		cellEditorParams: () => ({
			options: GROUP_PODCAST_EPISODE_TOPIC_OPTIONS,
			isMulti: true,
		}),
		columnMode:  ColumnModeTypeEnum.BOTH,
		valueGetter: params => params.data?.groupTargets?.groupTargetsPodcastEpisodeTopics,
		valueSetter: createValueSetter('groupTargets.groupTargetsPodcastEpisodeTopics'),
	},
	{
		field:              'groupTargets.groupTargetsSensitiveTopicExclusions.sensitiveTopics',
		headerName:         'Podcast content controls',
		cellRenderer:       SelectCellRenderer,
		editable:           editableOnThisLevel,
		cellRendererParams: (params) => ({
			labelGetter: (item) => item.label,
			disabled:    params.data.isCreate,
		}),
		elasticChild:     'settingsElastic',
		columnMode:       ColumnModeTypeEnum.BOTH,
		cellEditor:       AsyncCellSelectEditor,
		cellEditorParams: () => ({
			graphqlQuery:   SupergridGroupTargetsSensitiveTopicsDocument,
			valueFieldName: 'sensitiveTopicName',
		}),
		valueGetter: params => params.data?.groupTargets?.groupTargetsSensitiveTopicExclusions?.sensitiveTopics,
		valueSetter: createTypedOptionsValueSetter<SupergridGroupRowData>(
			'groupTargets.groupTargetsSensitiveTopicExclusions.sensitiveTopics',
			'SensitiveTopic',
		),
	},
	{
		field:              'groupBudget.type',
		headerName:         'Budget',
		elasticChild:       'settingsElastic',
		editable:           editableOnThisLevel,
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: (params) => ({
			options:  GROUP_BID_TYPE_OPTIONS,
			disabled: params.data.isCreate,
		}),
		cellEditor:       SelectCellEditor,
		cellEditorParams: () => ({
			options: GROUP_BID_TYPE_OPTIONS,
		}),
		columnMode:  ColumnModeTypeEnum.BOTH,
		valueGetter: params => params.data?.groupBudget?.type,
		valueSetter: (params) => {
			if (params.data?.groupBudget?.type !== undefined) {
				params.data.groupBudget.type = params.newValue;

				return true;
			}

			return false;
		},
	},
	/**
	 * From spotify docs about budget amount:
	 * The amount of your bid per 1000 impressions, multiplied by x10 to the 6th power.
	 * Behavior of this field depends on the bid_strategy specified.
	 * Ex: In order to set a bid of $20, you would specify a bid_micro_amount of 20000000.
	 */
	{
		field:              'groupBudget.microAmount',
		headerName:         'Budget value',
		editable:           editableOnThisLevel,
		elasticChild:       'settingsElastic',
		cellRenderer:       MoneyCellRenderer,
		cellRendererParams: (params) => ({
			divisor:        divideCurrencyFields.groupBudgetMicroAmount,
			placeholder:    'Unlimited',
			forcedCurrency: params.data?.groupBudget?.currency ?? params.data?.parent?.currency,
			disabled:       params.data.isCreate,
		}),
		valueGetter: params => params.data?.groupBudget?.microAmount,
		valueSetter(params) {
			if (params.data?.groupBudget?.microAmount !== undefined) {
				params.data.groupBudget.microAmount = params.newValue;

				return true;
			}

			return false;
		},
		cellEditor:       MoneyCellEditor,
		cellEditorParams: (params) => ({
			divisor:         divideCurrencyFields.groupBudgetMicroAmount,
			derivedCurrency: params.data?.groupBudget?.currency ?? params.data?.parent?.currency,
		}),
		columnMode: ColumnModeTypeEnum.BOTH,
	},
	{
		field:              'groupStartTime',
		headerName:         groupHeaderNames.groupStartTime,
		columnMode:         ColumnModeTypeEnum.BOTH,
		width:              226,
		editable:           editableOnThisLevel,
		cellEditor:         DateTimeCellEditor,
		cellRenderer:       DateCellRenderer,
		elasticChild:       'settingsElastic',
		cellRendererParams: params => ({
			...params,
			icon: true,
		}),
		cellEditorParams: params => ({
			minDate: dayjs().subtract(1, 'day'),
			initialValue:
				dayjs(params.data.groupStartTime).isValid()
					? dayjs(params.data.groupStartTime, DATE_TIME_ISO_FORMAT)
					: dayjs(undefined, DATE_TIME_ISO_FORMAT),
		}),
	},
	{
		field:              'groupEndTime',
		headerName:         groupHeaderNames.groupEndTime,
		columnMode:         ColumnModeTypeEnum.BOTH,
		width:              226,
		editable:           editableOnThisLevel,
		cellEditor:         DateTimeCellEditor,
		cellRenderer:       DateCellRenderer,
		elasticChild:       'settingsElastic',
		cellRendererParams: params => ({
			...params,
			icon: true,
		}),
		cellEditorParams: params => ({
			minDate:
				dayjs(params.data.groupStartTime).isValid()
					? dayjs(params.data.groupStartTime, DATE_TIME_ISO_FORMAT)
					: dayjs(undefined, DATE_TIME_ISO_FORMAT),
			initialValue:
				dayjs(params.data.groupStartTime).isValid()
					? dayjs(params.data.groupStartTime, DATE_TIME_ISO_FORMAT).add(1, 'day')
					: dayjs(undefined, DATE_TIME_ISO_FORMAT).add(1, 'day')
			,
		}),
	},
	{
		colId:              'groupFrequencyCapPerDay',
		// @ts-ignore: TODO: https://alephrnd.atlassian.net/browse/WB-4820
		field:              'groupFrequencyCapPerDay',
		headerName:         'Freq. cap /Day',
		elasticChild:       'settingsElastic',
		editable:           editableOnThisLevel,
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: (params) => ({
			options:  GROUP_FREQUENCY_CAP_PER_DAY_OPTIONS,
			disabled: params.data.isCreate,
		}),
		cellEditor:       SelectCellEditor,
		cellEditorParams: () => ({
			options: GROUP_FREQUENCY_CAP_PER_DAY_OPTIONS,
		}),
		columnMode:  ColumnModeTypeEnum.BOTH,
		valueGetter: (params) => getGroupFrequencyCapValue(params, GroupFrequencyUnit.Day),
		valueSetter: (params) => setGroupFrequencyCapValue(params, GroupFrequencyUnit.Day),
	},
	{
		colId:              'groupFrequencyCapPerWeek',
		// @ts-ignore: TODO: https://alephrnd.atlassian.net/browse/WB-4820
		field:              'groupFrequencyCapPerWeek',
		headerName:         'Freq. cap /Week',
		elasticChild:       'settingsElastic',
		editable:           editableOnThisLevel,
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: (params) => ({
			options:  GROUP_FREQUENCY_CAP_PER_WEEK_OPTIONS,
			disabled: params.data.isCreate,
		}),
		cellEditor:       SelectCellEditor,
		cellEditorParams: () => ({
			options: GROUP_FREQUENCY_CAP_PER_WEEK_OPTIONS,
		}),
		columnMode:  ColumnModeTypeEnum.BOTH,
		valueGetter: (params) => getGroupFrequencyCapValue(params, GroupFrequencyUnit.Week),
		valueSetter: (params) => setGroupFrequencyCapValue(params, GroupFrequencyUnit.Week),
	},
	{
		colId:              'groupFrequencyCapPerMonth',
		// @ts-ignore: TODO: https://alephrnd.atlassian.net/browse/WB-4820
		field:              'groupFrequencyCapPerMonth',
		headerName:         'Freq. cap /Month',
		elasticChild:       'settingsElastic',
		editable:           editableOnThisLevel,
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: (params) => ({
			options:  GROUP_FREQUENCY_CAP_PER_MONTH_OPTIONS,
			disabled: params.data.isCreate,
		}),
		cellEditor:       SelectCellEditor,
		cellEditorParams: () => ({
			options: GROUP_FREQUENCY_CAP_PER_MONTH_OPTIONS,
		}),
		columnMode:  ColumnModeTypeEnum.BOTH,
		valueGetter: (params) => getGroupFrequencyCapValue(params, GroupFrequencyUnit.Month),
		valueSetter: (params) => setGroupFrequencyCapValue(params, GroupFrequencyUnit.Month),
	},
	// For now hidden
	// More info: https://alephholding.slack.com/archives/C046JEDA003/p1754988706922949?thread_ts=1754988351.285239&cid=C046JEDA003
	// {
	// 	colId:              'groupGoal',
	// 	// @ts-ignore: TODO: https://alephrnd.atlassian.net/browse/WB-4820
	// 	field:              'groupGoal',
	// 	headerName:         'Delivery goal',
	// 	elasticChild:       'settingsElastic',
	// 	valueGetter:        (params) => params.data?.parent?.campaignObjective,
	// 	cellRenderer:       SelectCellRenderer,
	// 	cellRendererParams: params => ({
	// 		disabled: params?.data?.isCreate,
	// 		options:  SPOTIFY_CAMPAIGN_OBJECTIVE_OPTIONS,
	// 	}),
	// 	columnMode: ColumnModeTypeEnum.BOTH,
	// },
	{
		field:              'groupPacing',
		headerName:         'Pacing',
		elasticChild:       'settingsElastic',
		editable:           editableOnThisLevel,
		cellRenderer:       SelectCellRenderer,
		cellRendererParams: (params) => ({
			options:  GROUP_PACING_OPTIONS,
			disabled: params.data.isCreate,
		}),
		cellEditor:       SelectCellEditor,
		cellEditorParams: () => ({
			options: GROUP_PACING_OPTIONS,
		}),
		columnMode:  ColumnModeTypeEnum.BOTH,
		valueSetter: (params) => {
			if (params.data?.groupPacing) {
				params.data.groupPacing = params.newValue;

				return true;
			}

			return false;
		},
	},
	{
		field:              'groupBidMicroAmount',
		headerName:         'Bid',
		editable:           editableOnThisLevel,
		elasticChild:       'settingsElastic',
		cellRenderer:       MoneyCellRenderer,
		cellRendererParams: (params) => ({
			divisor:        divideCurrencyFields.groupBidMicroAmount,
			placeholder:    'Unset',
			forcedCurrency: params.data?.groupBudget?.currency ?? params.data?.parent?.currency,
			disabled:       params.data.isCreate,
		}),
		valueGetter: params => params.data?.groupBidMicroAmount,
		valueSetter(params) {
			if (params.data?.groupBidMicroAmount !== undefined) {
				params.data.groupBidMicroAmount = params.newValue;

				return true;
			}

			return false;
		},
		cellEditor:       MoneyCellEditor,
		cellEditorParams: (params) => ({
			divisor:         divideCurrencyFields.groupBidMicroAmount,
			derivedCurrency: params.data?.groupBudget?.currency ?? params.data?.parent?.currency,
		}),
		columnMode: ColumnModeTypeEnum.BOTH,
	},
];
